import * as React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { styled } from '@mui/material/styles';
import { Box, Toolbar, IconButton, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  GridOn,
  PictureAsPdf,
} from '@mui/icons-material';
import Loader from '../../../Loader';
import { markdownEditorExtensions } from './markdownEditorConfig';

// Lazy so the heavy @react-pdf (ESM) module only loads when the preview is
// opened — keeps the editor importable in unit tests and code-splits the
// preview out of the main bundle.
const PdfPreviewModal = React.lazy(() => import('./PdfPreviewModal'));

/**
 * TipTap-based rich-text editor (SHER-271 PR2).
 *
 * Drop-in replacement for the legacy `HTMLEditor` (contentEditable +
 * document.execCommand): same props contract, but the document model is a
 * ProseMirror schema, so only renderable constructs can be produced — the
 * unbounded/messy HTML that broke the PDF renderer can no longer be created.
 *
 * The value exchanged through `onChange` / `initialValue` is controlled by
 * `format`:
 *  - 'html'      (default) — emits clean, schema-constrained HTML. This is a
 *                 safe drop-in for the legacy HTMLEditor: the existing screen
 *                 and PDF renderers keep working, but the unrenderable junk
 *                 (font/span/style/o:p from Word/Translate) can no longer be
 *                 produced because it is not in the ProseMirror schema.
 *  - 'markdown'  — emits Markdown (underline as inline `<u>`), the canonical
 *                 artifact for the eventual storage migration.
 * Either way the editor accepts legacy HTML or Markdown as `initialValue`.
 */
interface TiptapEditorProps {
  initialValue?: string | null;
  loading: boolean;
  onChange: (value: string) => void;
  onBlur?: () => void;
  format?: 'html' | 'markdown';
}

const EditorWrapper = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  minHeight: '150px',
  '& .editor-toolbar': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.grey[50],
  },
  '& .ProseMirror': {
    padding: theme.spacing(0.75),
    minHeight: '100px',
    fontSize: '18px',
    lineHeight: 1.2,
    outline: 'none',
    '& p': { margin: '0 0 0.25em 0' },
    '& ul, & ol': { paddingLeft: '18px', margin: '0.25em 0' },
  },
}));

const getMarkdown = (editor: { storage: any }): string =>
  editor.storage?.markdown?.getMarkdown?.() ?? '';

const serialize = (editor: any, format: 'html' | 'markdown'): string =>
  format === 'markdown' ? getMarkdown(editor) : editor.getHTML();

const TiptapEditor: React.FC<TiptapEditorProps> = ({
  initialValue,
  loading,
  onChange,
  onBlur,
  format = 'html',
}) => {
  // The editor instance is created once; its handlers would otherwise close over
  // the first-render onChange/onBlur. redux-form passes a fresh input.onChange on
  // re-render, so route through refs to always call the latest props.
  const onChangeRef = React.useRef(onChange);
  const onBlurRef = React.useRef(onBlur);
  React.useEffect(() => {
    onChangeRef.current = onChange;
    onBlurRef.current = onBlur;
  });

  const editor = useEditor({
    extensions: markdownEditorExtensions,
    content: initialValue || '',
    onUpdate: ({ editor: e }) => onChangeRef.current(serialize(e, format)),
    onBlur: () => onBlurRef.current?.(),
  });

  // Sync external value changes (e.g. selecting a template from a dropdown)
  // without clobbering what the user is typing or looping on our own updates.
  React.useEffect(() => {
    if (!editor) return;
    const incoming = initialValue || '';
    if (incoming !== serialize(editor, format)) {
      // `false` = do not emit an update (avoids feedback into onChange)
      editor.commands.setContent(incoming, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, initialValue]);

  const [previewOpen, setPreviewOpen] = React.useState(false);

  if (loading) {
    return <Loader />;
  }

  const isActive = (name: string, attrs?: Record<string, unknown>) =>
    editor?.isActive(name, attrs) ? 'primary' : 'default';

  return (
    <EditorWrapper>
      <Toolbar className="editor-toolbar" variant="dense">
        <IconButton
          size="small"
          color={isActive('bold')}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          title="Bold (Ctrl+B)"
        >
          <FormatBold />
        </IconButton>
        <IconButton
          size="small"
          color={isActive('italic')}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          title="Italic (Ctrl+I)"
        >
          <FormatItalic />
        </IconButton>
        <IconButton
          size="small"
          color={isActive('underline')}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          title="Underline (Ctrl+U)"
        >
          <FormatUnderlined />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <IconButton
          size="small"
          color={isActive('bulletList')}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          <FormatListBulleted />
        </IconButton>
        <IconButton
          size="small"
          color={isActive('orderedList')}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          title="Numbered List"
        >
          <FormatListNumbered />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <IconButton
          size="small"
          onClick={() =>
            editor
              ?.chain()
              .focus()
              .insertTable({ rows: 2, cols: 3, withHeaderRow: false })
              .run()
          }
          title="Insertar tabla"
        >
          <GridOn />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setPreviewOpen(true)}
          title="Vista previa PDF"
        >
          <PictureAsPdf />
        </IconButton>
      </Toolbar>
      <EditorContent editor={editor} />
      {previewOpen && (
        <React.Suspense fallback={null}>
          <PdfPreviewModal
            open
            onClose={() => setPreviewOpen(false)}
            html={editor?.getHTML() ?? ''}
          />
        </React.Suspense>
      )}
    </EditorWrapper>
  );
};

export default TiptapEditor;
